import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useFamilyScope } from '@/contexts/FamilyScopeContext';
import { useFamilyCapabilities } from '@/hooks/useFamilyCapabilities';
import { isRouteAllowedForPath } from '@/lib/familyNavigation';

/**
 * Standalone screen shown when the active family role does not include the
 * area the member tried to open. Offers the two exits that always exist:
 * their own account, and the scanner when the role includes it.
 */
export function FamilyScopeBlocked() {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const { scope, setScope } = useFamilyScope();
  const caps = useFamilyCapabilities();

  const ROLE_LABEL: Record<string, string> = {
    owner: t('scope.role.owner'),
    co_manager: t('scope.role.coManager'),
    contributor: t('scope.role.contributor'),
    scanner: t('scope.role.scanner'),
    viewer: t('scope.role.viewer'),
  };
  const roleLabel = ROLE_LABEL[caps.role ?? ''] ?? t('scopeGuard.defaultRole');
  const canScan = caps.canScanReceipt;
  const groupName = scope.type === 'family' ? scope.groupName : t('scopeGuard.defaultGroupName');

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-md px-6 py-16 text-center">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-border/40 bg-card/60">
          <User className="h-5 w-5 text-primary" />
        </div>
        <h2 className="font-display text-xl text-foreground">{t('scopeGuard.title')}</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {t('scopeGuard.description', { group: groupName, role: roleLabel })}
        </p>
        <div className="mt-6 flex flex-col items-center gap-2">
          <Button
            onClick={() => {
              setScope({ type: 'personal' });
              navigate('/dashboard');
            }}
            className="w-full sm:w-auto"
          >
            <User className="me-2 h-4 w-4" />
            {t('scopeGuard.viewMyAccount')}
          </Button>
          {canScan && (
            <Button
              variant="outline"
              onClick={() => navigate('/scanner')}
              className="w-full sm:w-auto"
            >
              <Camera className="me-2 h-4 w-4" />
              {t('scopeGuard.scanForFamily', { group: groupName })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders children only when the active family scope allows the current route.
 *
 * Personal scope is never reshaped. Inside a family scope, a member whose role
 * does not include the area sees a short explanation plus a way back to their
 * own account instead of an empty or broken screen. Database policies already
 * block the underlying data; this keeps the interface honest.
 */
export function FamilyScopeGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { scope } = useFamilyScope();
  const caps = useFamilyCapabilities();

  if (scope.type !== 'family') return <>{children}</>;
  if (caps.loading) return <>{children}</>;
  if (isRouteAllowedForPath(pathname, caps, true)) return <>{children}</>;

  return <FamilyScopeBlocked />;
}
